import { useEffect, useState } from "react";
import { fileUrl, localPreviews, UploadBar, useUploadProgress } from "../files";
import { useBoardUI } from "../store";

const fmt = (n?: number) =>
  !n
    ? ""
    : n > 1e9
      ? `${(n / 1e9).toFixed(1)} GB`
      : n > 1e6
        ? `${(n / 1e6).toFixed(1)} MB`
        : `${Math.round(n / 1e3)} KB`;
const iconFor = (mime = "", name = "") => {
  const ext = name.split(".").pop()?.toUpperCase() ?? "";
  if (mime === "application/pdf") return "PDF";
  if (/zip|rar|7z|tar/.test(mime) || /ZIP|RAR|7Z/.test(ext)) return "ZIP";
  if (/word|officedocument\.word/.test(mime)) return "DOC";
  if (/sheet|excel|csv/.test(mime)) return "XLS";
  if (/presentation|powerpoint/.test(mime)) return "PPT";
  if (/photoshop|psd/i.test(mime + ext)) return "PSD";
  if (/illustrator|postscript/.test(mime) || ext === "AI") return "AI";
  return ext.slice(0, 4) || "FILE";
};

export function FileBody({
  id,
  fileId,
  name,
  mime,
  size,
  uploading,
}: {
  id: string;
  fileId?: string | null;
  name?: string;
  mime?: string;
  size?: number;
  uploading?: boolean;
}) {
  async function download() {
    if (!fileId) return;
    const u = await fileUrl(fileId, "original");
    window.open(u, "_blank", "noopener");
  }
  const pct = useUploadProgress(id);
  return (
    <div className="relative flex items-center gap-3 px-3 py-3" onDoubleClick={download}>
      <div className="display flex h-12 w-10 shrink-0 items-center justify-center rounded-[3px] border border-border bg-panel-3 text-[11px] tracking-wider">
        {iconFor(mime, name)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13px] font-semibold">{name}</div>
        <div className="text-[11px] text-muted">
          {uploading ? `⇡ ${pct ?? 0}% · ${fmt(size)}` : fmt(size)}
        </div>
        {uploading ? (
          <div className="mt-1 h-1 w-full rounded-sm bg-panel-3">
            <div className="h-1 rounded-sm bg-accent transition-[width]" style={{ width: `${pct ?? 2}%` }} />
          </div>
        ) : null}
      </div>
      {fileId ? (
        <button
          type="button"
          onClick={download}
          onPointerDown={(e) => e.stopPropagation()}
          className="rounded-sm border border-border px-2 py-1 text-[11px] text-muted hover:text-text"
          title={name}
        >
          ⇩
        </button>
      ) : null}
    </div>
  );
}

export function MediaBody({
  id,
  kind,
  fileId,
  uploading,
  name,
  autoplay,
  loop,
}: {
  id: string;
  kind: "video" | "audio";
  fileId?: string | null;
  uploading?: boolean;
  name?: string;
  autoplay?: boolean;
  loop?: boolean;
}) {
  const [src, setSrc] = useState<string | null>(localPreviews.get(id) ?? null);
  const selected = useBoardUI((s) => s.selection.has(id));
  useEffect(() => {
    if (!fileId) return;
    let alive = true;
    fileUrl(fileId, "original")
      .then((u) => alive && setSrc(u))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [fileId]);
  if (!src || uploading)
    return (
      <div className="relative flex h-40 items-center justify-center bg-panel-3 text-muted">
        {uploading ? <UploadBar id={id} label={name} /> : "…"}
      </div>
    );
  return kind === "video" ? (
    <video
      src={src}
      controls
      preload="metadata"
      playsInline
      autoPlay={autoplay}
      loop={loop}
      muted={autoplay}
      className="block w-full bg-black"
      onPointerDown={(e) => selected && e.stopPropagation()}
      title={name}
    >
      <track kind="captions" />
    </video>
  ) : (
    <div className="px-3 py-3">
      <div className="mb-2 truncate text-[12px] text-muted">{name}</div>
      <audio
        src={src}
        controls
        preload="metadata"
        autoPlay={autoplay}
        loop={loop}
        className="w-full"
        onPointerDown={(e) => selected && e.stopPropagation()}
      >
        <track kind="captions" />
      </audio>
    </div>
  );
}
